// The want page's re-point queue (#222; spec
// docs/superpowers/specs/2026-11-02-wantlist-redesign-design.md, Decisions
// "Narrowing is offered on the page"). The card options' picks re-point a
// want's Card Specificity one write at a time, in pick order, whether or not
// the options are still open. A re-point onto a specificity the list already
// holds merges the want into that entry (the server's dedup): the queue then
// follows it to its survivor, so a pick queued for the old want, or made
// afterwards from options still showing it, goes to the survivor, after both
// wants' earlier writes. No state of the page's: the caller's `write` does
// the request and resolves to the entry that answered; its `apply` puts that
// entry on the page once the queue has recorded any merge, so `survivorOf`
// already sees the merge when the rows swap. The merges live in the map the
// caller passes (a reactive one, so a view deriving from `survivorOf` is
// told of a merge).

/**
 * @template {{ id: number }} E
 * @typedef {(id: number) => Promise<E | null>} RepointWrite - re-point want
 *   `id` (its survivor, at write time); resolves to the entry that answered
 *   (the same id, or the one it merged into), or null when refused
 */

/**
 * @template {{ id: number }} E
 * @param {{ merges?: Map<number, number> }} [opts] - where merges are kept
 *   (a merged want -> the entry it merged into)
 * @returns {{
 *   enqueue: (id: number, write: RepointWrite<E>, apply?: (id: number, entry: E) => void) => Promise<void>,
 *   survivorOf: (id: number) => number,
 * }}
 */
export function createRepointQueue({ merges = new Map() } = {}) {
  /** @type {Map<number, Promise<void>>} the last write queued per want */
  const chains = new Map();

  /** @param {number} id */
  function survivorOf(id) {
    let at = id;
    while (merges.has(at)) at = /** @type {number} */ (merges.get(at));
    return at;
  }

  /**
   * Queue one write for want `id`, after any queued for it (or its
   * survivor). `apply(id, entry)` runs when the write is answered (not when
   * it is refused), after any merge is recorded, with the want it re-pointed
   * and the entry that answered. Resolves once this one is written, refused
   * or failed; never rejects, so a failure ends this write only, never the
   * ones after it.
   * @param {number} id
   * @param {RepointWrite<E>} write
   * @param {(id: number, entry: E) => void} [apply]
   * @returns {Promise<void>}
   */
  function enqueue(id, write, apply) {
    const key = survivorOf(id);
    const run = (chains.get(key) ?? Promise.resolve()).then(async () => {
      const at = survivorOf(id);
      try {
        const answer = await write(at);
        if (!answer) return;
        if (answer.id !== at) {
          merges.set(at, answer.id);
          // The survivor's own writes still pending and this want's queued
          // ones both come before anything picked for it from now on.
          chains.set(
            answer.id,
            Promise.all([chains.get(answer.id), chains.get(at)]).then(() => {})
          );
        }
        apply?.(at, answer);
      } catch (e) {
        console.error('[WantPage] Re-point failed:', e);
      }
    });
    chains.set(key, run);
    return run;
  }

  return { enqueue, survivorOf };
}
